/**
 * The add-a-center page. Owner-only on the server; the form's Save 403s
 * cleanly for a non-owner rather than being hidden.
 *
 * A create is more than a row write now: it verifies or registers the
 * doctor's Health-360 account, may write their subscription end and may
 * provision a database — and a step after the center row exists can fail
 * without undoing it. So the response is shown, not navigated past: its
 * warnings are the only record of what did not happen, and its next steps are
 * what still stands between a saved center and one that serves images.
 *
 * ── Compaction pass ─────────────────────────────────────────────────────────
 * The header's paragraph is gone: it described allocation and the save/apply
 * split, and the form says both itself — the "Auto-assigned" block shows the
 * allocation as it happens and the callout above Save states that nothing goes
 * live until Plan applies it. Repeating them here only pushed the first field
 * below the fold.
 *
 * The `centers-form` wrapper is a styling hook and nothing else;
 * `page-centers.css` compacts `CenterForm`'s layout through descendant
 * selectors scoped under this class, so the edge-node wizard, which reuses the
 * form's restriction editor, is not touched by any of it.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Callout } from '../components/Callout';
import { CenterForm, EMPTY_CENTER } from '../components/CenterForm';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/ui/Button';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { pluralise } from '../lib/format';
import type { CenterCreateResponse } from '../types/api';

import '../styles/page-centers.css';

/** The server says what happened; a false boolean could mean "not needed" or
 *  "failed", and this card is what an operator reads as the record. */
const EXPIRY_OUTCOME: Record<CenterCreateResponse['expiry_status'], string> = {
  written: 'Written to their record',
  unchanged: 'Unchanged — it already matched',
  failed: 'NOT written — see the warning above',
};

const DATABASE_OUTCOME: Record<CenterCreateResponse['database_status'], string> = {
  created: 'Created',
  skipped: 'Not requested',
  unavailable: 'Not created — no clinic database server is configured',
  failed: 'FAILED — see the warning above',
};

export function CenterNewPage() {
  const [created, setCreated] = useState<CenterCreateResponse | null>(null);

  return (
    <>
      <PageHeader title={created ? `Center ${created.slug} created` : 'New center'} />
      {created ? (
        <CreatedCenter result={created} onAnother={() => setCreated(null)} />
      ) : (
        <div className="centers-form">
          <CenterForm
            mode="create"
            initial={EMPTY_CENTER}
            onCreated={(response) => {
              setCreated(response);
              // Save is at the foot of a long form; the result is at the head.
              window.scrollTo({ top: 0 });
            }}
          />
        </div>
      )}
    </>
  );
}

function CreatedCenter({
  result,
  onAnother,
}: {
  result: CenterCreateResponse;
  onAnother: () => void;
}) {
  const centerPath = `/centers/${encodeURIComponent(result.slug)}`;
  const warningCount = result.warnings.length;

  return (
    <div className="stack">
      <Callout
        tone="good"
        live
        title={`Center ${result.slug} created for doctor #${result.doctor_id} (${
          result.doctor_created ? 'new account' : 'existing account'
        })`}
      >
        {result.pending}
      </Callout>

      {warningCount > 0 ? (
        <Callout
          tone="warning"
          title={`${warningCount} ${pluralise(warningCount, 'thing')} did not go as planned`}
        >
          <ul className="reasons">
            {result.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </Callout>
      ) : null}

      <Card>
        <CardHeader title="What was saved" />
        <CardBody>
          <dl className="kv">
            <dt>Domain</dt>
            <dd>
              <span className="mono">{result.domain}</span>
            </dd>
            <dt>Doctor account</dt>
            <dd>
              #{result.doctor_id} —{' '}
              {result.doctor_created
                ? 'registered just now; they will receive Health-360 welcome emails'
                : 'an existing account, password verified'}
            </dd>
            <dt>Subscription end</dt>
            <dd>{EXPIRY_OUTCOME[result.expiry_status]}</dd>
            <dt>Database</dt>
            <dd>{DATABASE_OUTCOME[result.database_status]}</dd>
          </dl>
        </CardBody>
      </Card>

      {result.next_steps.length > 0 ? (
        <Card>
          <CardHeader title="Next steps" />
          <CardBody>
            <ol className="reasons">
              {result.next_steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          </CardBody>
        </Card>
      ) : null}

      <div className="row">
        <Link to={centerPath} className="btn btn--primary btn--md">
          Open {result.slug}
        </Link>
        <Button kind="tertiary" onClick={onAnother}>
          Create another center
        </Button>
      </div>
    </div>
  );
}
